/**
 * Finding the individual cuts on a butcher's chart.
 *
 * A grading standard comes as a grid, which a splitter handles. A butcher's
 * chart does not: the cuts are scattered around a carcass diagram with leader
 * lines to their names, at whatever sizes fit. No grid lies over that.
 *
 * What every such chart does have is separate photographs on one flat
 * background. That is a connected-components problem and not a machine-learning
 * one — reaching for a model here would mean training something to find what a
 * threshold already finds exactly.
 *
 * WHAT THIS DELIBERATELY DOES NOT DO
 *
 * It does not read the labels. OCR on "SCHOUDER" against "SCHOUDERKARBONADE" is
 * a coin flip between two real cuts, and a misread puts the wrong photograph
 * against the wrong class where nothing downstream will ever contradict it. The
 * regions come back unnamed and a person assigns them.
 *
 * It also does not know which region is meat. The carcass diagram in the middle
 * of the chart is a large connected region and comes back like any other; so
 * does a logo. Both are for the person to drop.
 */

export interface Plate {
  x: number;
  y: number;
  w: number;
  h: number;
  /** Foreground pixels over bounding-box area. Low means a thin shape — a
      leader line or a bracket rather than a photograph. */
  fill: number;
  /**
   * What this region looks like.
   *
   * `photo` — solid and roughly square: a cut.
   * `label`  — wide, thin and sparse: a printed name.
   *
   * Told apart by shape alone, never by reading. The regions come back as
   * pictures either way; a person reads the word and drags it onto the cut it
   * belongs to, which is the part of a butcher's chart that actually takes
   * effort — the leader lines cross and are hard to follow by eye.
   */
  kind: 'photo' | 'label';
}

export interface DetectOptions {
  /** Fraction of the image's smaller side a region must exceed. */
  minSide?: number;
  /** And must not exceed, which is how the carcass gets a chance to be dropped. */
  maxSide?: number;
  /** How different from the background a pixel must be to count. */
  tolerance?: number;
  /** Longest side used for the scan. Detection does not need full resolution. */
  workingSize?: number;
}

/**
 * The background colour, sampled from the edges.
 *
 * The median of the border rather than a fixed white: these charts come out of a
 * scanner or a phone camera and the paper is pale green, warm grey, off-white.
 * Assuming white makes a green poster one enormous foreground region.
 */
function backgroundOf(data: Uint8ClampedArray, w: number, h: number): [number, number, number] {
  const rs: number[] = [];
  const gs: number[] = [];
  const bs: number[] = [];
  const take = (x: number, y: number) => {
    const i = (y * w + x) * 4;
    rs.push(data[i]!);
    gs.push(data[i + 1]!);
    bs.push(data[i + 2]!);
  };
  const step = Math.max(1, Math.floor(Math.min(w, h) / 60));
  for (let x = 0; x < w; x += step) {
    take(x, 0);
    take(x, h - 1);
  }
  for (let y = 0; y < h; y += step) {
    take(0, y);
    take(w - 1, y);
  }
  const mid = (xs: number[]) => xs.sort((a, b) => a - b)[Math.floor(xs.length / 2)] ?? 255;
  return [mid(rs), mid(gs), mid(bs)];
}

/** One morphological pass. `dx`/`dy` are radii in pixels. */
function morph(src: Uint8Array, w: number, h: number, dx: number, dy: number, erode: boolean) {
  const out = new Uint8Array(w * h);
  for (let y = 0; y < h; y += 1) {
    for (let x = 0; x < w; x += 1) {
      let hit = erode ? 1 : 0;
      for (let ky = -dy; ky <= dy && hit === (erode ? 1 : 0); ky += 1) {
        const yy = y + ky;
        if (yy < 0 || yy >= h) continue;
        for (let kx = -dx; kx <= dx; kx += 1) {
          const xx = x + kx;
          if (xx < 0 || xx >= w) continue;
          const v = src[yy * w + xx]!;
          if (erode ? !v : v) {
            hit = erode ? 0 : 1;
            break;
          }
        }
      }
      out[y * w + x] = hit;
    }
  }
  return out;
}

/** Bounding boxes of the connected components of a mask. */
function components(mask: Uint8Array, w: number, h: number) {
  const seen = new Uint8Array(w * h);
  const stack: number[] = [];
  const boxes: { x0: number; y0: number; x1: number; y1: number; n: number }[] = [];
  for (let start = 0; start < mask.length; start += 1) {
    if (!mask[start] || seen[start]) continue;
    stack.length = 0;
    stack.push(start);
    seen[start] = 1;
    let x0 = w;
    let y0 = h;
    let x1 = 0;
    let y1 = 0;
    let n = 0;
    while (stack.length) {
      const p = stack.pop()!;
      const x = p % w;
      const y = (p - x) / w;
      n += 1;
      if (x < x0) x0 = x;
      if (x > x1) x1 = x;
      if (y < y0) y0 = y;
      if (y > y1) y1 = y;
      if (x > 0 && mask[p - 1] && !seen[p - 1]) (seen[p - 1] = 1), stack.push(p - 1);
      if (x < w - 1 && mask[p + 1] && !seen[p + 1]) (seen[p + 1] = 1), stack.push(p + 1);
      if (y > 0 && mask[p - w] && !seen[p - w]) (seen[p - w] = 1), stack.push(p - w);
      if (y < h - 1 && mask[p + w] && !seen[p + w]) (seen[p + w] = 1), stack.push(p + w);
    }
    boxes.push({ x0, y0, x1, y1, n });
  }
  return boxes;
}

export async function detectPlates(
  file: File | Blob,
  options: DetectOptions = {},
): Promise<{ plates: Plate[]; width: number; height: number }> {
  const { minSide = 0.05, maxSide = 0.6, tolerance = 38, workingSize = 900 } = options;

  const bitmap = await createImageBitmap(file);
  const scale = Math.min(1, workingSize / Math.max(bitmap.width, bitmap.height));
  const w = Math.max(1, Math.round(bitmap.width * scale));
  const h = Math.max(1, Math.round(bitmap.height * scale));

  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d', { willReadFrequently: true })!;
  ctx.drawImage(bitmap, 0, 0, w, h);
  const { data } = ctx.getImageData(0, 0, w, h);
  const [br, bg, bb] = backgroundOf(data, w, h);

  const fg = new Uint8Array(w * h);
  for (let i = 0, p = 0; i < data.length; i += 4, p += 1) {
    const d = Math.abs(data[i]! - br) + Math.abs(data[i + 1]! - bg) + Math.abs(data[i + 2]! - bb);
    fg[p] = d > tolerance ? 1 : 0;
  }

  const minPx = Math.min(w, h) * minSide;
  const maxPx = Math.min(w, h) * maxSide;
  const plates: Plate[] = [];

  /**
   * PASS ONE — the photographs.
   *
   * An opening: erode, then dilate. The erosion snaps the leader lines that run
   * from every cut to the carcass, and without it the whole sheet is ONE region.
   * That was the first thing this got wrong, and it was invisible from the
   * result — ten cuts became one blob that the size filter then discarded, so
   * the page reported nothing found rather than everything joined.
   */
  const opened = morph(morph(fg, w, h, 2, 2, true), w, h, 2, 2, false);
  const closedPhotos = morph(morph(opened, w, h, 2, 2, false), w, h, 2, 2, true);
  for (const b of components(closedPhotos, w, h)) {
    const bw = b.x1 - b.x0 + 1;
    const bh = b.y1 - b.y0 + 1;
    if (bw > maxPx && bh > maxPx) continue;
    if (bh < minPx || bw < minPx) continue;
    if (b.n / (bw * bh) < 0.35) continue;
    plates.push({
      x: Math.round(b.x0 / scale),
      y: Math.round(b.y0 / scale),
      w: Math.round(bw / scale),
      h: Math.round(bh / scale),
      fill: Number((b.n / (bw * bh)).toFixed(3)),
      kind: 'photo',
    });
  }

  /**
   * PASS TWO — the printed names.
   *
   * The solid regions are removed first, then what is left is dilated
   * HORIZONTALLY so the letters of one word join into a line.
   *
   * Fill is deliberately not tested here, and that was the second thing this got
   * wrong. A word is sparse — background between the letters — so the obvious
   * rule is "sparse and wide". But the dilation that joins the letters fills
   * exactly those gaps, so by the time the region exists its fill is 0.98. The
   * test rejected every label on the sheet for being too solid, which is a
   * property this code had just created. Shape alone decides: long, thin, and
   * shorter than a photograph.
   */
  const solid = morph(morph(morph(fg, w, h, 3, 3, true), w, h, 3, 3, false), w, h, 2, 2, false);
  const thin = new Uint8Array(w * h);
  for (let p = 0; p < fg.length; p += 1) thin[p] = fg[p] && !solid[p] ? 1 : 0;
  const words = morph(thin, w, h, 6, 0, false);

  for (const b of components(words, w, h)) {
    const bw = b.x1 - b.x0 + 1;
    const bh = b.y1 - b.y0 + 1;
    if (bw / bh < 3) continue; // not a line of type
    if (bw < minPx * 1.2) continue; // a dash, or one short word fragment
    if (bh < 4 || bh > minPx * 0.6) continue; // a rule, or a slice of a photograph
    plates.push({
      x: Math.round(b.x0 / scale),
      y: Math.round(b.y0 / scale),
      w: Math.round(bw / scale),
      h: Math.round(bh / scale),
      fill: Number((b.n / (bw * bh)).toFixed(3)),
      kind: 'label',
    });
  }

  // Reading order: rows first, then left to right within a row.
  const rowHeight = Math.max(...plates.filter((p) => p.kind === 'photo').map((p) => p.h), 1) * 0.6;
  plates.sort((a, b) => (Math.abs(a.y - b.y) > rowHeight ? a.y - b.y : a.x - b.x));

  return { plates, width: bitmap.width, height: bitmap.height };
}

/** Cut one detected region out of the original image, with a little breathing room. */
export async function cropPlate(
  file: File | Blob,
  plate: Plate,
  padding = 0.04,
): Promise<string> {
  const bitmap = await createImageBitmap(file);
  const px = Math.round(plate.w * padding);
  const py = Math.round(plate.h * padding);
  const x = Math.max(0, plate.x - px);
  const y = Math.max(0, plate.y - py);
  const w = Math.min(bitmap.width - x, plate.w + px * 2);
  const h = Math.min(bitmap.height - y, plate.h + py * 2);

  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  canvas.getContext('2d')!.drawImage(bitmap, x, y, w, h, 0, 0, w, h);
  return canvas.toDataURL('image/jpeg', 0.92);
}
